/* import __COLOCATED_TEMPLATE__ from './select-fins-email.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { type PulseAccordion } from 'glint/pulse';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import { isEmpty, isPresent } from '@ember/utils';
import safeWindowOpen from 'embercom/lib/safe-window-open';

interface Args {
  accordion: PulseAccordion;
  onContinue: () => void;
}

export default class StandaloneSalesforceSelectFinsEmail extends Component<Args> {
  @service store: $TSFixMe;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;
  @service declare intl: IntlService;

  @tracked newSupportEmail?: string;

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get salesforceConfig() {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get salesforceSetupData() {
    return this.salesforcePlatform.salesforceSetupData;
  }

  get isLoading() {
    return this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceSetupData;
  }

  get isDisabled() {
    return (
      this.salesforceConfig?.isPendingSetup ||
      !this.salesforceSetupData?.hasPermissionSetAssignedToInstaller
    );
  }

  get isComplete() {
    return this.salesforceConfig?.isConnected && isPresent(this.selectedSupportEmail);
  }

  get isRequired() {
    if (!this.appService.app.hasOptionalConfigureEmails) {
      return true;
    }
    return isPresent(this.salesforcePlatform.selectedSalesforceCaseOrigins);
  }

  get supportEmailOptions() {
    let supportEmails = this.salesforceSetupData.supportEmails?.map(({ address, is_freemail }) => ({
      text: address,
      value: address,
      isDisabled: is_freemail,
      tooltipText:
        is_freemail &&
        this.intl.t(
          'standalone.deploy.salesforce.cases.configure-emails.select-fins-email-section.from-email-address.freemail-tooltip',
        ),
    })) as { text: string; value: string; isDisabled?: boolean; tooltipText?: string }[];

    // Handle case where the selected email is not in the list
    if (
      this.selectedSupportEmail !== undefined &&
      !supportEmails.find((email) => email.value === this.selectedSupportEmail)
    ) {
      supportEmails.push({
        text: this.selectedSupportEmail,
        value: this.selectedSupportEmail,
        isDisabled: true,
        tooltipText: this.intl.t(
          'standalone.deploy.salesforce.cases.configure-emails.select-fins-email-section.from-email-address.support-email-tooltip',
        ),
      });
    }

    if (isEmpty(supportEmails)) {
      return [
        {
          text: this.intl.t(
            'standalone.deploy.salesforce.cases.configure-emails.select-fins-email-section.from-email-address.no-emails-available',
          ),
          value: '',
          isDisabled: true,
        },
      ];
    }

    return supportEmails;
  }

  get selectedSupportEmail(): string | undefined {
    return this.salesforceConfig.supportEmail || undefined;
  }

  @action continue() {
    if (this.args.onContinue) {
      this.args.onContinue();
    }
  }

  @action selectSupportEmail(supportEmail: string) {
    if (supportEmail === this.selectedSupportEmail) {
      return;
    }

    this.newSupportEmail = supportEmail;
  }

  @action
  async continueAndMaybeSave() {
    try {
      if (this.newSupportEmail) {
        let response = await this.salesforceConfig.setSupportEmail(this.newSupportEmail);
        this.store.push(this.store.normalize('dkim-settings', response.dkim_settings));
        this.store.pushPayload({
          'custom-bounce-settings': response.custom_bounce_settings,
          'custom-email-address': response.support_email_address,
        });
        this.notificationsService.notifyConfirmation(
          this.intl.t(
            'standalone.deploy.salesforce.cases.configure-emails.select-fins-email-section.from-email-address.success-notification',
          ),
        );
        this.newSupportEmail = undefined;
      }

      this.continue();
    } catch (e) {
      this.notificationsService.notifyResponseError(e, {
        default: this.intl.t(
          'standalone.deploy.salesforce.cases.configure-emails.select-fins-email-section.from-email-address.error-notification',
        ),
      });
    }
  }

  @action openEmailToCaseSettingsUrl() {
    safeWindowOpen(this.salesforceConfig.emailToCaseSettingsUrl, '_blank');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Cases::ConfigureEmails::SelectFinsEmail': typeof StandaloneSalesforceSelectFinsEmail;
    'standalone/deploy/salesforce/cases/configure-emails/select-fins-email': typeof StandaloneSalesforceSelectFinsEmail;
  }
}
